import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    getDeficiencyHistory,
    formatChangeType,
    formatFieldName,
    formatHistoryDate,
    formatFieldValue,
} from './services/deficiencyHistoryService';

/**
 * DeficiencyHistory component displays the revision history of a deficiency
 * Shows all changes made to the deficiency over time with details about what changed
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Object} props.deficiency - The deficiency object
 * @returns {React.ReactElement} DeficiencyHistory component
 */
const DeficiencyHistory = ({ open, onClose, deficiency }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch history when dialog opens
    useEffect(() => {
        if (open && deficiency?.deficiencyId) {
            fetchHistory();
        }
    }, [open, deficiency?.deficiencyId]);

    const fetchHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const historyData = await getDeficiencyHistory(deficiency.deficiencyId);

            // Sort by revision number in descending order (most recent first)
            const sortedHistory = historyData.sort((a, b) => {
                return parseInt(b.revisionNumber) - parseInt(a.revisionNumber);
            });

            setHistory(sortedHistory);
        } catch (err) {
            setError('Failed to load deficiency history');
            // Error is already captured in state for user display
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setHistory([]);
        setError(null);
        onClose();
    };

    const renderChangeDetails = (historyItem) => {
        const { changedFields, oldValues, newValues, changeType } = historyItem;

        // Special handling for activity events (IMAGE_ADDED, IMAGE_REMOVED, COMMENT_ADDED)
        if (
            changeType === 'IMAGE_ADDED' ||
            changeType === 'IMAGE_REMOVED' ||
            changeType === 'COMMENT_ADDED'
        ) {
            const activityData = newValues || oldValues;
            const activityKey = Object.keys(activityData || {})[0];
            const activityValue = activityData?.[activityKey];

            if (!activityValue) return null;

            return (
                <Box sx={{ mt: 2 }}>
                    <Box
                        sx={{
                            p: 1.5,
                            backgroundColor: changeType === 'IMAGE_REMOVED' ? '#fff5f5' : '#f0fff4',
                            borderRadius: 1.5,
                            border:
                                changeType === 'IMAGE_REMOVED'
                                    ? '1px solid #fed7d7'
                                    : '1px solid #9ae6b4',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                color: changeType === 'IMAGE_REMOVED' ? '#c53030' : '#22543d',
                            }}
                        >
                            {formatFieldName(activityKey)}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#2d3748',
                                wordBreak: 'break-word',
                            }}
                        >
                            {activityValue}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        // If this is a creation entry and backend returned only newValues
        // render the new values even if changedFields is empty
        if (
            changeType === 'DEFICIENCY_CREATED' &&
            (!changedFields || Object.keys(changedFields).length === 0) &&
            newValues &&
            Object.keys(newValues).length > 0
        ) {
            const fields = Object.keys(newValues);
            return (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Initial Values:
                    </Typography>
                    <Stack spacing={1.5}>
                        {fields.map((field) => (
                            <Box
                                key={field}
                                sx={{
                                    p: 1.5,
                                    backgroundColor: '#f0fff4',
                                    borderRadius: 1.5,
                                    border: '1px solid #9ae6b4',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, mb: 1, color: '#22543d' }}
                                >
                                    {formatFieldName(field)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#2d3748' }}>
                                    {formatFieldValue(field, newValues[field])}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            );
        }

        if (!changedFields || Object.keys(changedFields).length === 0) {
            return null;
        }

        // Regular update events
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Changes Made:
                </Typography>
                <Stack spacing={1.5}>
                    {Object.keys(changedFields).map((field) => (
                        <Box
                            key={field}
                            sx={{
                                p: 1.5,
                                backgroundColor: '#f8f9fa',
                                borderRadius: 1.5,
                                border: '1px solid #e9ecef',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: '#495057',
                                }}
                            >
                                {formatFieldName(field)}
                            </Typography>

                            {/* From/To values in a clean vertical layout */}
                            <Stack spacing={1}>
                                {oldValues && oldValues[field] !== undefined && (
                                    <Box
                                        sx={{
                                            p: 1,
                                            backgroundColor: '#fff5f5',
                                            borderRadius: 1,
                                            border: '1px solid #fed7d7',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#c53030',
                                                fontWeight: 500,
                                                display: 'block',
                                                mb: 0.5,
                                            }}
                                        >
                                            Previous Value:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                wordBreak: 'break-word',
                                                color: '#2d3748',
                                            }}
                                        >
                                            {formatFieldValue(field, oldValues[field])}
                                        </Typography>
                                    </Box>
                                )}

                                {newValues && newValues[field] !== undefined && (
                                    <Box
                                        sx={{
                                            p: 1,
                                            backgroundColor: '#f0fff4',
                                            borderRadius: 1,
                                            border: '1px solid #9ae6b4',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#22543d',
                                                fontWeight: 500,
                                                display: 'block',
                                                mb: 0.5,
                                            }}
                                        >
                                            New Value:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                wordBreak: 'break-word',
                                                color: '#2d3748',
                                            }}
                                        >
                                            {formatFieldValue(field, newValues[field])}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Box>
        );
    };

    const getChangeTypeColor = (changeType) => {
        const colorMap = {
            DEFICIENCY_CREATED: 'success',
            DEFICIENCY_UPDATE: 'primary',
            IMAGE_ADDED: 'success',
            IMAGE_REMOVED: 'warning',
            COMMENT_ADDED: 'info',
            STATUS_CHANGE: 'info',
            ASSIGNMENT_CHANGE: 'secondary',
        };
        return colorMap[changeType] || 'default';
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 2,
                    px: 3,
                    pt: 3,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: '#1a202c',
                        fontSize: '1.25rem',
                    }}
                >
                    Deficiency History
                </Typography>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: '#718096',
                        '&:hover': {
                            backgroundColor: '#f7fafc',
                            color: '#4a5568',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}

                {!loading && !error && history.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#718096',
                                fontWeight: 500,
                            }}
                        >
                            No history available for this deficiency.
                        </Typography>
                    </Box>
                )}

                {!loading && !error && history.length > 0 && (
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={2.5}>
                            {history.map((historyItem, index) => (
                                <Card
                                    key={`${historyItem.revisionNumber}-${index}`}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 2,
                                        border: '1px solid #e9ecef',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                                        '&:hover': {
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
                                            transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                mb: 2.5,
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                        color: '#1a202c',
                                                    }}
                                                >
                                                    Revision {historyItem.revisionNumber}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#718096',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {formatHistoryDate(historyItem.dateStamp)}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={formatChangeType(historyItem.changeType)}
                                                color={getChangeTypeColor(historyItem.changeType)}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    height: 28,
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 2 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#4a5568',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Modified by:{' '}
                                                <span style={{ fontWeight: 600, color: '#2d3748' }}>
                                                    {historyItem.modifiedByUserName}
                                                </span>
                                            </Typography>
                                        </Box>

                                        {renderChangeDetails(historyItem)}
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        color: '#4a5568',
                        borderColor: '#cbd5e0',
                        '&:hover': {
                            borderColor: '#a0aec0',
                            backgroundColor: '#f7fafc',
                        },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 3,
                        py: 1,
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeficiencyHistory;
